import { logoImages } from '@/assets/images'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { PageLoader } from '@/components/ui/loader'
import { useTheme } from '@/hooks/use-theme'
import { useUnsavedChangesWarning } from '@/hooks/use-unsaved-changes-warning'
import { cn } from '@/lib/utils'
import { verifyOtpSchema, type VerifyOtpSchemaType } from '@/schema/auth/signup'
import { scrollToTop } from '@/utils/scroll-to-top'
import { useSignUp } from '@clerk/clerk-react'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { toast } from 'sonner'

export function OTPVerificationPage() {
  const { theme } = useTheme()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { signUp, isLoaded, setActive } = useSignUp()
  const [email, setEmail] = useState('')
  const [isVerificationComplete, setIsVerificationComplete] = useState(false)
  const [resendDisabled, setResendDisabled] = useState(true)
  const [resendCountdown, setResendCountdown] = useState(30)

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<VerifyOtpSchemaType>({
    resolver: zodResolver(verifyOtpSchema),
  })

  useUnsavedChangesWarning({ when: isDirty && !isSubmitting })
  // Get email from URL params or localStorage
  useEffect(() => {
    const emailFromParams = searchParams.get('email')
    const emailFromStorage = localStorage.getItem('signup_email')

    if (emailFromParams) {
      setEmail(emailFromParams)
      localStorage.setItem('signup_email', emailFromParams)
    } else if (emailFromStorage) {
      setEmail(emailFromStorage)
    } else {
      // No email found, redirect to signup
      navigate('/sign-up', { replace: true })
      toast.error('No email found', {
        description: 'Please start the signup process again.',
      })
    }
  }, [searchParams, navigate])

  // Check if verification is already complete
  useEffect(() => {
    if (isLoaded && signUp) {
      if (signUp.status === 'complete') {
        setIsVerificationComplete(true)
        localStorage.removeItem('signup_email')
        setActive({
          session: signUp.createdSessionId,
          redirectUrl: '/',
        })
        toast.success('Welcome to CISO Adapt!', {
          description: 'Your email has been verified and you are now signed up!',
        })
      }
    }
  }, [isLoaded, signUp, setActive])

  // Countdown effect for resend button
  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => {
        setResendCountdown(resendCountdown - 1)
      }, 1000)
      return () => clearTimeout(timer)
    } else {
      setResendDisabled(false)
    }
  }, [resendCountdown])

  const handleResendVerification = async () => {
    try {
      if (!isLoaded || !signUp) return

      await signUp.prepareEmailAddressVerification()
      toast.success('Verification code resent!', {
        description: 'Please check your email for the new verification code.',
      })

      // Start 30 second countdown
      setResendDisabled(true)
      setResendCountdown(30)
    } catch (err) {
      toast.error('Failed to resend verification', {
        description: err instanceof Error ? err.message : 'Please try again later.',
      })
    }
  }

  const handleBackToSignup = () => {
    localStorage.removeItem('signup_email')
    navigate('/sign-up', { replace: true })
  }

  const onSubmit = async (data: VerifyOtpSchemaType) => {
    try {
      if (!isLoaded || !signUp || !data.verificationCode) {
        toast.error('Please enter the verification code', {
          description: 'Enter the 6-digit code from your email.',
        })
        return
      }

      const result = await signUp.attemptEmailAddressVerification({
        code: data.verificationCode,
      })

      if (result?.status === 'complete') {
        setIsVerificationComplete(true)
        localStorage.removeItem('signup_email')
        await setActive({
          session: result.createdSessionId,
          redirectUrl: '/',
        })
        toast.success('Welcome to CISO Adapt!', {
          description: 'Your email has been verified and you are now signed up!',
        })
      } else {
        toast.error('Verification failed', {
          description: 'Please check the code and try again.',
        })
      }
    } catch (err) {
      // Check if it's already verified
      if (err instanceof Error && err.message.includes('already verified')) {
        setIsVerificationComplete(true)
        localStorage.removeItem('signup_email')
        toast.success('Welcome to CISO Adapt!', {
          description: 'Your email was already verified!',
        })
        navigate('/', { replace: true })
      } else {
        toast.error('Verification failed', {
          description: err instanceof Error ? err.message : 'Please check the code and try again.',
        })
      }
    }
  }

  if (!isLoaded) return <PageLoader />

  if (!email) return <PageLoader />

  if (isVerificationComplete) return <PageLoader />

  return (
    <div className='flex min-h-screen w-full items-center justify-center bg-background'>
      <div className={cn('flex  flex-col gap-6 xl:w-[60%] lg:w-[80%]  md:w-[60%] w-full')}>
        <Card className='overflow-hidden p-0 shadow-md'>
          <CardContent className='grid p-0 grid-cols-1'>
            <div className='flex flex-col items-center text-center gap-4 p-6 md:p-8'>
              <div className='flex flex-col items-center text-center'>
                <img
                  loading='lazy'
                  decoding='async'
                  src={theme === 'dark' ? logoImages.logoLight : logoImages.logoBlack}
                  alt='Image'
                  className='h-12 w-auto'
                />

                <h1 className='text-2xl font-medium pt-5'>Check your email</h1>
                <p className='text-muted-foreground text-sm'>
                  We've sent a verification code to <strong>{email}</strong>
                </p>
                <p className='text-muted-foreground text-xs mt-2'>
                  Enter the 6-digit code from your email to complete your signup.
                </p>
              </div>

              <div className='flex flex-col gap-3 w-full max-w-sm'>
                <form
                  onSubmit={handleSubmit(onSubmit, () => {
                    scrollToTop()
                  })}
                  className='flex flex-col gap-3'
                >
                  <div className='space-y-1'>
                    <label htmlFor='verification-code' className='text-sm font-medium'>
                      Enter verification code
                    </label>
                    <div className='pt-1'></div>
                    <Input
                      id='verification-code'
                      type='text'
                      placeholder='Enter the 6-digit code'
                      {...register('verificationCode')}
                      maxLength={6}
                      className='text-center text-lg tracking-widest'
                      disabled={isSubmitting || isVerificationComplete}
                      error={errors.verificationCode?.message}
                    />
                  </div>
                  <Button
                    type='submit'
                    disabled={isSubmitting || watch('verificationCode')?.length !== 6}
                    className='w-full'
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className='w-4 h-4 animate-spin' />
                        <span>Verifying...</span>
                      </>
                    ) : (
                      'Verify Email'
                    )}
                  </Button>
                </form>
                <Button
                  variant='outline'
                  onClick={handleResendVerification}
                  disabled={resendDisabled}
                  className='w-full'
                >
                  {resendDisabled ? `Resend in ${resendCountdown}s` : 'Resend verification code'}
                </Button>
                <Button variant='ghost' onClick={handleBackToSignup} className='w-full'>
                  Back to signup
                </Button>
              </div>

              <div className='text-center text-sm'>
                Already have an account?&nbsp;
                <Link to='/sign-in' className='underline underline-offset-4'>
                  Sign in
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>
        <div className='text-muted-foreground *:[a]:hover:text-primary text-center text-xs text-balance *:[a]:underline *:[a]:underline-offset-4'>
          By clicking continue, you agree to our <Link to='/terms-of-service'>Terms of Service</Link> and&nbsp;
          <Link to='/privacy-policy'>Privacy Policy</Link>.
        </div>
      </div>
    </div>
  )
}
